// @ts-nocheck
import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileSpreadsheet, X, ArrowRight, Settings, Check, Sparkles, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { supabase } from '../../utils/supabaseClient';
import logger from '../../utils/logger';
import ConfigCategoryPreview from '../ConfigCategoryPreview';
import { SessionExpiredError } from '../../utils/sessionExpiredError';
import './FileUpload.css';

const FileUpload = ({ onFileUpload, onPeriodDiscovery, onProcessStart, onError, loading, userEmail }) => {
  const [file, setFile] = useState(null);
  const [sheetName, setSheetName] = useState('');
  const [availableSheets, setAvailableSheets] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [statementType, setStatementType] = useState(''); // '' = unselected, 'income_statement'
  
  // Configuration selection state
  const [availableConfigs, setAvailableConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [useDefault, setUseDefault] = useState(false);

  // Fetch available configurations when statement type changes
  useEffect(() => {
    if (statementType && userEmail) {
      fetchConfigurations(statementType);
    } else {
      setAvailableConfigs([]);
      setSelectedConfig(null);
      setUseDefault(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementType, userEmail]);

  // Fetch configurations for the user's tenant
  const fetchConfigurations = async (configType) => {
    setLoadingConfigs(true);
    try {
      // Get current session for JWT token
      await supabase.auth.getSession();
      
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/tenant-config/list/${encodeURIComponent(userEmail)}`,
        {
          params: { config_type: configType },
          withCredentials: true,
          headers: {
            'x-api-key': import.meta.env.VITE_API_KEY,
          }
        }
      );
      
      const configs = response.data.configs || [];
      setAvailableConfigs(configs);
      
      // Auto-select the first config if available
      if (configs.length > 0) {
        setSelectedConfig(configs[0]);
        setUseDefault(false);

      } else {
        setSelectedConfig(null);
        setUseDefault(true);
      }
    } catch (error) {
      logger.error('❌ Error fetching configurations:', error);
      // Don't show error to user - just fall back to default
      setAvailableConfigs([]);
      setSelectedConfig(null);
      setUseDefault(true);
    } finally {
      setLoadingConfigs(false);
    }
  };

  // Fetch available sheet names from the uploaded file
  const fetchSheetNames = async (uploadedFile) => {
    setLoadingSheets(true);
    const formData = new FormData();
    formData.append('file', uploadedFile);

    try {
      // Get current session for JWT token
      await supabase.auth.getSession();
      
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/get-sheets/`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-api-key': import.meta.env.VITE_API_KEY,
          }
        }
      );
      const sheets = response.data.sheets || [];
      setAvailableSheets(sheets);
      // Auto-select first sheet if available
      if (sheets.length > 0) {
        setSheetName(sheets[0]);
      }
    } catch (error) {
      if (error instanceof SessionExpiredError) return;
      logger.error('❌ Error fetching sheets:', error);
      onError('Failed to read Excel sheets. Please check the file.');
      setAvailableSheets([]);
    } finally {
      setLoadingSheets(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      const ext = uploadedFile.name.toLowerCase();
      if (ext.endsWith('.xlsx') || ext.endsWith('.xlsm') || ext.endsWith('.csv')) {
        setFile(uploadedFile);
        // Fetch sheet names when file is uploaded
        fetchSheetNames(uploadedFile);
      } else {
        onError('Please upload an Excel file (.xlsx, .xlsm) or CSV file (.csv)');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    disabled: loading
  });

  const removeFile = () => {
    setFile(null);
    setStatementType('');
    setSheetName('');
    setAvailableSheets([]);
    setSelectedConfig(null);
    setUseDefault(false);
    setAvailableConfigs([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      onError('Please select a file first');
      return;
    }

    if (!sheetName.trim()) {
      onError('Please enter a sheet name');
      return;
    }

    if (!statementType) {
      onError('Please select a statement type');
      return;
    }

    const endpoint = '/discover-periods/';
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sheet_name', sheetName);

    try {
      onProcessStart();
      
      // Get current session for JWT token
      await supabase.auth.getSession();
      
      const response = await axios.post(`${import.meta.env.VITE_BACKEND_BASE_URL}${endpoint}`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-api-key': import.meta.env.VITE_API_KEY,
        },
        timeout: 120000,
      });

      onPeriodDiscovery(response.data, 'pl');
      // Pass selected config along with file data
      onFileUpload(file, sheetName, statementType, 'pl', selectedConfig);
    } catch (error) {
      if (error instanceof SessionExpiredError) return;
      logger.error('❌ Error:', error);
      onError(error.response?.data?.detail || error.message);
    }
  };

  // Format category count text
  const getCategoryCountText = (config) => {
    if (!config?.categories) return '0 categories';
    const count = config.categories.length;
    return `${count} ${count === 1 ? 'category' : 'categories'}`;
  };

  // Check if LLM guidelines are available
  const hasLLMGuidelines = (config) => {
    return config?.llm_guidelines_available || (config?.llm_guidelines && config.llm_guidelines.length > 100);
  };

  return (
    <div className="ccp-layout">
      <div className={`file-upload-container border-beam-container ${loading ? 'loading' : ''}`}>
        <div className="border-beam"></div>
        <div className="upload-header">
          <div className="header-icon">
            <BarChart3 size={24} />
          </div>
          <div>
            <h3>Upload Financial Data</h3>
            <p className="upload-description">Upload your Excel file to analyze financial data</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="upload-form">
          {/* File Drop Zone */}
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? 'active' : ''} ${file ? 'has-file' : ''} ${loading ? 'disabled' : ''}`}
          >
            <input {...getInputProps()} />
            {file ? (
              <div className="file-info">
                <FileSpreadsheet size={48} className="file-icon" />
                <div className="file-details">
                  <p className="file-name">{file.name}</p>
                  <p className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <button type="button" onClick={removeFile} className="remove-file-btn" disabled={loading}>
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="dropzone-content">
                <Upload size={48} className="upload-icon" />
                <p className="dropzone-text">
                  {isDragActive ? "Drop file here..." : "Drag & drop Excel or CSV file"}
                </p>
              </div>
            )}
          </div>

          {/* Sheet Selector - Only show after file upload */}
          {file && (
            <div className="form-group">
              <label className="statement-type-label">Sheet: <span className="required">*</span></label>
              {loadingSheets ? (
                <div className="sheet-loading">
                  <div className="button-spinner"></div>
                  <span>Loading sheets...</span>
                </div>
              ) : availableSheets.length > 0 ? (
                <div className="sheet-list-selector">
                  {availableSheets.map((sheet) => (
                    <div
                      key={sheet}
                      className={`sheet-option ${sheetName === sheet ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
                      onClick={() => !loading && setSheetName(sheet)}
                    >
                      <div className="radio-circle">
                        {sheetName === sheet && <div className="radio-dot" />}
                      </div>
                      <span className="sheet-option-name">{sheet}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="sheet-empty-state">
                  No sheets found in file
                </div>
              )}
            </div>
          )}

          {/* Statement Type Selector - Only show after file upload */}
          {file && (
            <div className="form-group">
              <label className="statement-type-label">Statement Type: <span className="required">*</span></label>
              <div className="statement-type-selector">
                <div
                  className={`statement-option ${statementType === 'income_statement' ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
                  onClick={() => !loading && setStatementType('income_statement')}
                >
                  <div className="radio-circle">{statementType === 'income_statement' && <div className="radio-dot" />}</div>
                  <span className="statement-text">Income Statement</span>
                </div>
              </div>
            </div>
          )}

          {/* Configuration Selector */}
          {statementType && (
            <div className="form-group config-selector-group">
              <label className="statement-type-label">
                <Settings size={14} className="label-icon" />
                Chart of Accounts Configuration:
              </label>

              {loadingConfigs ? (
                <div className="config-loading">
                  <div className="button-spinner"></div>
                  <span>Loading configurations...</span>
                </div>
              ) : (
                <div className="config-selector">
                  <div className="config-options">
                    {availableConfigs.map((config) => (
                      <div
                        key={config.id}
                        className={`config-option ${selectedConfig?.id === config.id ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
                        onClick={() => {
                          if (!loading) {
                            setSelectedConfig(config);
                            setUseDefault(false);
                          }
                        }}
                      >
                        <div className="config-option-header">
                          <div className="radio-circle">
                            {selectedConfig?.id === config.id && <div className="radio-dot" />}
                          </div>
                          <div className="config-option-info">
                            <span className="config-name">
                              {config.config_name || config.tenant_name || 'My Configuration'}
                            </span>
                            <span className="config-meta">
                              {getCategoryCountText(config)}
                              {hasLLMGuidelines(config) && (
                                <span className="config-badge ai-badge">
                                  <Sparkles size={12} />
                                  AI Guidelines
                                </span>
                              )}
                            </span>
                          </div>
                          {selectedConfig?.id === config.id && (
                            <Check size={18} className="config-check" />
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Default Configuration Option */}
                    <div
                      className={`config-option config-option-default ${useDefault ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
                      onClick={() => {
                        if (!loading) {
                          setSelectedConfig(null);
                          setUseDefault(true);
                        }
                      }}
                    >
                      <div className="config-option-header">
                        <div className="radio-circle">
                          {useDefault && <div className="radio-dot" />}
                        </div>
                        <div className="config-option-info">
                          <span className="config-name">Use Default Configuration</span>
                          <span className="config-meta">Standard categories for P&L</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="analyze-button btn-animated"
            disabled={loading || !file || !statementType || !sheetName.trim()}
          >
            {loading ? (
              <>
                <div className="button-spinner"></div>
                Analyzing...
              </>
            ) : (
              <>
                Analyze Sheet Structure
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>
      </div>

      {(selectedConfig || useDefault) && statementType && (
        <div className="ccp-layout-side">
          <ConfigCategoryPreview
            config={selectedConfig}
            isDefault={useDefault}
            configType={statementType}
            mode="panel"
          />
        </div>
      )}
    </div>
  );
};

export default FileUpload;